import React, { useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import { useApiQuery } from '../../../../../../api/hooks';
import { Endpoints, QueryKey } from '../../../../../../views/democracy/constants';
import { extractErrorMessage, buildMediaUrl } from '../../../../../../views/democracy/utils';
import { adminBuySanction } from '../../../../../../views/democracy/controlsDispatcher';
import { normalizeSanctionTypes, resolveSanctionTypeLabel } from '../sanctions/sanctionsModel';
import './shop.css';

// Магазин санкций (пульт): drag тёмной карточки санкции в корзину команды, высота = число кругов.
export default function ManageSanctions({ democracy, teams }) {
    const { enqueueSnackbar } = useSnackbar();
    const democracyId = democracy.id;

    const { data: typesRaw } = useApiQuery(QueryKey.sanctionTypes, Endpoints.sanctions.types, { refetchOnMount: 'always' });
    const { data: rawList } = useApiQuery(
        QueryKey.sanctions(democracyId),
        Endpoints.sanctions.listForDemocracy(democracyId),
        { refetchOnMount: 'always', enabled: !!democracyId }
    );

    const sanctions = useMemo(() => (rawList || []).filter(s => s && s.id != null), [rawList]);
    const byId = useMemo(() => new Map(sanctions.map(s => [s.id, s])), [sanctions]);
    const types = useMemo(() => normalizeSanctionTypes(typesRaw).sort((a, b) => (a.orderId || 0) - (b.orderId || 0)), [typesRaw]);

    const [currentType, setCurrentType] = useState(null);
    useEffect(() => { if (!currentType && types.length) setCurrentType(types[0].name); }, [types, currentType]);

    const teamCount = teams.length;
    const [carts, setCarts] = useState(() => Array.from({ length: teamCount }, () => []));
    const cartsRef = useRef(carts);
    cartsRef.current = carts;
    useEffect(() => { setCarts(Array.from({ length: teamCount }, () => [])); }, [teamCount]);

    const teamsRef = useRef(teams);
    teamsRef.current = teams;

    const dragRef = useRef(null);
    const [dragView, setDragView] = useState(null);
    const [dragging, setDragging] = useState(false);

    const startDrag = (sanction, e) => {
        e.preventDefault();
        dragRef.current = { sanction, overTeam: null, rounds: 1, blocked: false };
        setDragView({ x: e.clientX, y: e.clientY, sanction, overTeam: null, rounds: 1, blocked: false });
        setDragging(true);
    };

    useEffect(() => {
        if (!dragging) return undefined;
        const onMove = (e) => {
            const d = dragRef.current;
            if (!d) return;
            const el = document.elementFromPoint(e.clientX, e.clientY);
            const cartEl = el?.closest('[data-cart-team]');
            let overTeam = null;
            let rounds = 1;
            let blocked = false;
            if (cartEl) {
                overTeam = Number(cartEl.dataset.cartTeam);
                const teamActive = teamsRef.current[overTeam]?.activeSanctions || [];
                blocked = teamActive.some(a => byId.get(a.sanctionId)?.sanctionType === d.sanction.sanctionType)
                    || (cartsRef.current[overTeam] || []).some(it => byId.get(it.sanctionId)?.sanctionType === d.sanction.sanctionType);
                if (!blocked) {
                    const rect = cartEl.getBoundingClientRect();
                    const frac = (rect.bottom - e.clientY) / rect.height;
                    rounds = Math.min(8, Math.max(1, Math.ceil(frac * 8)));
                }
            }
            Object.assign(d, { overTeam, rounds, blocked });
            setDragView({ x: e.clientX, y: e.clientY, sanction: d.sanction, overTeam, rounds, blocked });
        };
        const onUp = () => {
            const d = dragRef.current;
            if (d && d.overTeam != null && !d.blocked) {
                setCarts(prev => {
                    const next = prev.map(a => a.slice());
                    next[d.overTeam].push({ sanctionId: d.sanction.id, rounds: d.rounds });
                    return next;
                });
            }
            dragRef.current = null;
            setDragView(null);
            setDragging(false);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    }, [dragging, byId]);

    const removeFromCart = (teamIndex, sanctionId) => {
        setCarts(prev => prev.map((arr, i) => (i === teamIndex ? arr.filter(it => it.sanctionId !== sanctionId) : arr)));
    };

    const clearAll = () => setCarts(teams.map(() => []));

    const handleBuyAll = async () => {
        const jobs = [];
        carts.forEach((arr, i) => {
            const targetTeamId = teams[i].teamId || teams[i].id;
            arr.forEach(it => jobs.push(adminBuySanction(democracyId, { sanctionId: it.sanctionId, duration: it.rounds, targetTeamId })));
        });
        if (!jobs.length) return;
        try {
            await Promise.all(jobs);
            enqueueSnackbar('Санкции выданы', { variant: 'success' });
            clearAll();
        } catch (error) {
            enqueueSnackbar(extractErrorMessage(error, 'Не удалось выдать санкции'), { variant: 'error' });
        }
    };

    const typeSanctions = sanctions.filter(s => s.sanctionType === currentType);
    const allItems = carts.flatMap((arr, i) => arr.map(it => ({ ...it, teamIndex: i })));
    const totalCost = allItems.reduce((sum, it) => sum + (byId.get(it.sanctionId)?.cost || 0) * it.rounds, 0);

    return (
        <div className="shop">
            <h4>Магазин санкций</h4>

            {allItems.length > 0 && (
                <div className="cart-bar">
                    <div className="cb-top">
                        <div className="cb-ttl">Корзина <span className="tot">· {allItems.length} поз. · {totalCost} б.</span></div>
                        <div className="cb-acts">
                            <button type="button" className="cbtn cbtn-blue" onClick={handleBuyAll}>Купить всё</button>
                            <button type="button" className="cbtn cbtn-ghost" onClick={clearAll}>Сбросить</button>
                        </div>
                    </div>
                    <div className="cb-cols">
                        {teams.map((team, i) => {
                            if (!carts[i].length) return null;
                            const sum = carts[i].reduce((s, it) => s + (byId.get(it.sanctionId)?.cost || 0) * it.rounds, 0);
                            return (
                                <div className="tcol" key={team.teamId || team.id} style={{ '--tc': team.color }}>
                                    <div className="tlab"><span>{team.name}</span><span className="sum">{sum} б.</span></div>
                                    {carts[i].map(it => {
                                        const s = byId.get(it.sanctionId);
                                        return (
                                            <div className="row" key={it.sanctionId}>
                                                <span className="t">{s?.title}</span>
                                                <span className="r">{it.rounds}×{s?.cost}</span>
                                                <button type="button" className="x" onClick={() => removeFromCart(i, it.sanctionId)}>×</button>
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            <div className="shop-body">
                <div className="shop-grid">
                    {typeSanctions.map(s => {
                        const imgUrl = s.media?.mediaFileUrl ? buildMediaUrl(s.media.mediaFileUrl) : null;
                        return (
                            <div className="s-card" key={s.id} onMouseDown={(e) => startDrag(s, e)}>
                                {imgUrl && <div className="ill" style={{ backgroundImage: `url(${imgUrl})` }} />}
                                <div className="card-top">
                                    <div className="title">{s.title}</div>
                                    {s.description && <div className="descr">{s.description}</div>}
                                </div>
                                <div className="foot"><span className="cost">{s.cost} б.</span><span className="grip">⠿⠿</span></div>
                            </div>
                        );
                    })}
                </div>
                <div className="carts-grid">
                    {teams.map((team, i) => {
                        const cur = carts[i].find(it => byId.get(it.sanctionId)?.sanctionType === currentType);
                        const cs = cur ? byId.get(cur.sanctionId) : null;
                        const mImg = cs?.media?.mediaFileUrl ? buildMediaUrl(cs.media.mediaFileUrl) : null;
                        const activeOfType = (team.activeSanctions || []).find(a => byId.get(a.sanctionId)?.sanctionType === currentType);
                        const acs = activeOfType ? byId.get(activeOfType.sanctionId) : null;
                        const aImg = acs?.media?.mediaFileUrl ? buildMediaUrl(acs.media.mediaFileUrl) : null;
                        const aBuyerColor = activeOfType?.buyerTeamId
                            ? teams.find(t => (t.teamId || t.id) === activeOfType.buyerTeamId)?.color
                            : null;
                        const over = dragView?.overTeam === i;
                        const cls = `cart${over ? ' dragover' : ''}${over && dragView.blocked ? ' blocked' : ''}${over && !dragView.blocked ? ' hover' : ''}`;
                        return (
                            <div className={cls} data-cart-team={i} key={team.teamId || team.id} style={{ '--tc': team.color }}>
                                {activeOfType ? (
                                    <div className="mini-card active" style={aBuyerColor ? { '--tc': aBuyerColor } : undefined}>
                                        {aImg && <div className="ill" style={{ backgroundImage: `url(${aImg})` }} />}
                                        <div className="m-top">
                                            <div className="mt">{acs?.title || activeOfType.title}</div>
                                            {acs?.description && <div className="md">{acs.description}</div>}
                                        </div>
                                        <div className="mf">
                                            <span className="cost">куплено</span>
                                            <span className="r">{activeOfType.durationLeft}×</span>
                                        </div>
                                    </div>
                                ) : cur ? (
                                    <div className="mini-card">
                                        {mImg && <div className="ill" style={{ backgroundImage: `url(${mImg})` }} />}
                                        <div className="m-top">
                                            <div className="mt">{cs?.title}</div>
                                            {cs?.description && <div className="md">{cs.description}</div>}
                                        </div>
                                        <div className="mf">
                                            <span className="cost">{cs?.cost} б.</span>
                                            <span className="r">{cur.rounds}×</span>
                                        </div>
                                        <button type="button" className="x" onClick={() => removeFromCart(i, cur.sanctionId)}>×</button>
                                    </div>
                                ) : (
                                    <div className="cart-empty"><b>{team.name}</b>группа свободна</div>
                                )}
                                <div className="blocked-overlay">Группа занята</div>
                                <div className="overlay-zones">
                                    {[8, 7, 6, 5, 4, 3, 2, 1].map(z => (
                                        <div className={`zone${over && !dragView.blocked && z <= dragView.rounds ? ' active' : ''}`} data-zone={z} key={z}>
                                            {over && !dragView.blocked && z <= dragView.rounds ? z : ''}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="shop-tabs">
                {types.map(type => {
                    const count = sanctions.filter(s => s.sanctionType === type.name).length;
                    return (
                        <button type="button" key={type.name} className={`stab${type.name === currentType ? ' on' : ''}`} onClick={() => setCurrentType(type.name)}>
                            {resolveSanctionTypeLabel(type)}<span className="cnt">{count}</span>
                        </button>
                    );
                })}
            </div>

            {dragView && (
                <div className="drag-ghost" style={{ left: dragView.x + 14, top: dragView.y + 14 }}>
                    {dragView.sanction.title}
                    <span className="gc">{(dragView.sanction.cost || 0) * dragView.rounds} б.</span>
                    <div className="gh">
                        {dragView.overTeam != null ? `→ ${teams[dragView.overTeam]?.name} · ${dragView.rounds} кр.` : 'наведи на команду, высота = круги'}
                    </div>
                </div>
            )}
        </div>
    );
}

ManageSanctions.propTypes = {
    democracy: PropTypes.object.isRequired,
    teams: PropTypes.array.isRequired
};
